import { Component, type ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { Button } from "@components/ui/button";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export class AppErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    Sentry.captureException(error, {
      extra: { componentStack: errorInfo.componentStack },
      tags: { source: "AppErrorBoundary" },
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center p-12 h-full min-h-screen">
          <div className="max-w-md w-full text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mb-4">
              <ExclamationTriangleIcon className="size-7 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-base-600">
              Something went wrong
            </h2>
            <p className="text-sm text-base-500 mt-2">
              An unexpected error occurred. Please try reloading the page.
            </p>
            <div className="flex items-center justify-center gap-4 mt-6">
              <Button onClick={() => window.location.reload()}>
                Reload page
              </Button>
              <a
                href="/support"
                className="text-sm text-base-500 hover:text-base-700 transition-colors"
              >
                Contact support
              </a>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
